// Retry function to help reduce the frequency of chunk load issues occurring and the instances recorded in Sentry.
import React, { useCallback, useState, ComponentType, createContext } from 'react';
import { get, kebabCase, isEmpty } from 'lodash/fp';
import { Link, Redirect, NavLink } from 'react-router-dom';
import { MessageDescriptor, IntlShape } from 'react-intl';
import ReactHtmlParser from 'react-html-parser';
import { USER_CONTINENT, USER_COUNTRY } from '@whitelabel/helpers/constants';
import api, { API_HOST } from '@whitelabel/xcover-shared/helpers/api';
import { ERROR_CODE, HELP_LOGIN_MODAL_DISPLAYED } from '@whitelabel/xcover-shared/helpers/constants';
import { IShouldLogInArgs } from './types';
import loginMsg from './messages/loginMsg';

export const retry = (
  fn: (...args: any[]) => any,
  retriesLeft = 5,
  interval = 1000,
): Promise<{ default: ComponentType<any> }> =>
  new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: any) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });

export async function storeUserGeoIpInfo(): Promise<void> {
  try {
    const { countryCode, continentCode } = await api.get(`${API_HOST}/geoip/`, false);
    if (countryCode) {
      sessionStorage.setItem(USER_COUNTRY, countryCode);
    }
    if (continentCode) {
      sessionStorage.setItem(USER_CONTINENT, continentCode);
    }
  } catch {
    // ignore geoip error
  }
}

export const getCookie = (cookieName: any) => document.cookie.split(';').find((item) => item.includes(cookieName));

export const contentBlocksToNavItems = (blocks: any, pathPrefix = '') => {
  let currentRootItem = '';

  const navItems = blocks
    .map(({ value }: any) => value)
    .flat()
    .filter(({ type, value }: any) => type === 'heading' && (value.headingTag === 'h2' || value.headingTag === 'h3'))
    .reduce((items: any, { value: { title, headingTag } }: any) => {
      const key = kebabCase(title);
      const path = pathPrefix ? `${pathPrefix}-${key}` : key;
      const item = { key, name: title, path };

      if (headingTag === 'h2') {
        currentRootItem = key;
      }

      if (!currentRootItem || key === currentRootItem) {
        return { ...items, [key]: item };
      }

      const siblingItems = get(`${currentRootItem}.items`, items) || [];
      const rootItem = { ...items[currentRootItem], items: [...siblingItems, item] };

      return {
        ...items,
        [currentRootItem]: rootItem,
      };
    }, {});

  return Object.values(navItems);
};

export const PartnerIDContext = createContext({ partnerID: undefined });

export const renderDisclaimerContent = (content: any) =>
  content?.includes('<p>') ? ReactHtmlParser(content) : <p>{content}</p>;

export function formatPartnerMessage(
  intl: IntlShape,
  descriptor: MessageDescriptor,
  slug: string | undefined,
  value?: Record<string, any>,
): string {
  const defaultMessage = intl.formatMessage(descriptor, value);
  if (!slug) {
    return defaultMessage;
  }
  const descriptorId = descriptor.id;
  const partnerDescriptorId = `partners.${slug}.${descriptorId}`;
  const partnerMessage = intl.formatMessage(
    {
      id: partnerDescriptorId,
      defaultMessage,
    },
    value,
  );

  return partnerMessage === partnerDescriptorId ? defaultMessage : partnerMessage;
}

export const shouldLogIn = ({
  user,
  securityToken,
  locale,
  location,
  hasQueryParams,
}: IShouldLogInArgs): JSX.Element | undefined => {
  if (!user && !securityToken) {
    let search = '';
    if (hasQueryParams) {
      const params = new URLSearchParams(location.search);
      search = params.toString() || '';
    }
    return <Redirect to={{ pathname: `/${locale}/login`, search, state: { referrer: location } }} />;
  }
  return undefined;
};

/**
 * Remove the email to be empty string if that email is our system generated
 */
export const filterGeneratedEmail = (email: string | undefined) => (email?.includes('@xcover.com') ? '' : email);

export const useOpenLoginModal = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const openLoginModal = useCallback(() => {
    const loginModalDisplayed = sessionStorage.getItem(HELP_LOGIN_MODAL_DISPLAYED);
    if (!loginModalDisplayed) {
      setIsLoginModalOpen(true);
    }
  }, []);
  return [isLoginModalOpen, setIsLoginModalOpen, openLoginModal] as const;
};

export const getNormalOrReactRouterLinkProp = (tag: React.ReactNode, locale: string, path: string) =>
  tag === Link || tag === NavLink
    ? {
        to: `/${locale}${path}`,
      }
    : {
        href: `/${locale}${path}`,
      };

export const getCustomerErrorMsg = (intl: IntlShape, error: Record<string, any>, customErrorMsg = '') => {
  let errMessage = '';
  if (error?.code) {
    switch (error.code) {
      case ERROR_CODE.EMAIL_NOT_FOUND:
        errMessage = intl.formatMessage(loginMsg.invalidEmailAddressMessage);
        break;
      case ERROR_CODE.PHONE_NOT_FOUND:
        errMessage = intl.formatMessage(loginMsg.invalidMobileNumberMessage);
        break;
      // no default
    }
  } else if (error && !isEmpty(error)) {
    const firstErrorMsg = Object.values(error).find((value) => Array.isArray(value));
    if (firstErrorMsg) errMessage = firstErrorMsg[0] as string;
  }

  return errMessage || customErrorMsg || error?.message;
};
